import { useWindowResize } from '@toggle/helpers';
import * as d3 from 'd3';
import React, { useEffect, useMemo, useRef } from 'react';

import { StyledComponent } from '~/common/styled-component';

import { drawThresholdFill } from '../multi-line-chart/utils';
import { PriceChangesChartAxes } from './components/price-changes-chart-axes/PriceChangesChartAxes';
import { PriceChangesChartLinePlot } from './components/price-changes-line-chart-line-plot/PriceChangesChartLinePlot';
import * as S from './PriceChangesChart.styles';
import { PriceChangesChartLine, TsPoint } from './PriceChangesChart.types';

const THRESHOLD = 0;

const PriceChangesChartFormatters = {
  percent1f: (v: number) => `${d3.format('.1f')(v)}%`,
  percent2f: (v: number) => `${d3.format('.2f')(v)}%`,
  percent: (v: number) => `${d3.format('.0f')(v)}%`,
  default: (v: number) => v.toString(),
};

export type PriceChangesChartProps = {
  lines: PriceChangesChartLine[];
  yAxisFormatType?: keyof typeof PriceChangesChartFormatters;
  thresholdData?: TsPoint[];
  fillColors?: {
    positive: string;
    negative: string;
  };
  locale?: string;
  isMarketReaction?: boolean;
  verticalLines?: { x: Date }[];
};

export const PriceChangesChart: StyledComponent<
  PriceChangesChartProps,
  typeof S
> = ({
  lines,
  yAxisFormatType = 'default',
  thresholdData,
  fillColors,
  locale,
  verticalLines = [],
}) => {
  const containerRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { width: w, height: h } = useWindowResize({ targetRef: containerRef });

  const MARGIN = { top: 10, right: 50, bottom: 30, left: 20 };
  const chartWidth = w - MARGIN.right - MARGIN.left;
  const chartHeight = h - MARGIN.top - MARGIN.bottom;

  const mergedData = lines.reduce((acc, line) => {
    return acc.concat(line.data);
  }, [] as TsPoint[]);
  const uniqueDates = Array.from(new Set(mergedData.map(point => point.date)));

  // Create a map from date timestamp to index for gap removal
  const dateToIndexMap = useMemo(() => {
    const map = new Map<number, number>();
    uniqueDates.forEach((date, index) => {
      map.set(date.getTime(), index);
    });
    return map;
  }, [uniqueDates]);

  const [minY = 0, maxY = 0] = useMemo(
    () => d3.extent(mergedData.map(p => p.value)),
    [mergedData]
  );

  const [minX = new Date(), maxX = new Date()] = useMemo(
    () => d3.extent(mergedData.map(p => p.date)),
    [mergedData]
  );

  const xScaleTime = useMemo(() => {
    return d3.scaleTime().domain([minX, maxX]).range([0, chartWidth]);
  }, [minX, maxX, chartWidth]);

  const xScale = useMemo(() => {
    const domainEnd = uniqueDates.length - 1;
    return d3.scaleLinear().domain([0, domainEnd]).range([0, chartWidth]);
  }, [uniqueDates, chartWidth]);

  // ensures data points are not at the very edges of the chart
  const yPadding = 0.05 * Math.max(Math.abs(maxY), Math.abs(minY));
  const yScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain([minY - yPadding, maxY + yPadding])
      .range([chartHeight, 0]);
  }, [minY, maxY, h]);

  useEffect(() => {
    if (!canvasRef.current) {
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    if (!thresholdData?.length || !fillColors) {
      ctx.clearRect(0, 0, chartWidth, chartHeight);
      return;
    }

    drawThresholdFill(
      ctx,
      thresholdData,
      xScale,
      yScale,
      chartWidth,
      chartHeight,
      THRESHOLD,
      fillColors,
      dateToIndexMap
    );
  }, [
    thresholdData,
    xScale,
    yScale,
    chartWidth,
    chartHeight,
    canvasRef.current,
    fillColors,
    dateToIndexMap,
  ]);

  return (
    <S.PriceChangesChartRoot>
      <S.PriceChangesChartContainer
        ref={containerRef as React.RefObject<HTMLDivElement>}
      >
        {!!w && !!h && (
          <S.CanvasOverlay
            ref={canvasRef}
            width={chartWidth}
            height={chartHeight}
            $marginTop={MARGIN.top}
            $marginLeft={MARGIN.left}
          />
        )}
        <S.Plot data-testid="multi-line-chart">
          <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
            <PriceChangesChartAxes
              xScale={xScale}
              yScale={yScale}
              yAxisFormatter={PriceChangesChartFormatters[yAxisFormatType]}
              locale={locale}
              dateToIndexMap={dateToIndexMap}
            />
            {!!w &&
              !!h &&
              lines.map(line => (
                <PriceChangesChartLinePlot
                  key={line.label}
                  xScale={xScale}
                  yScale={yScale}
                  data={line.data}
                  color={line.color}
                  dashed={line.isBenchmark}
                  dateToIndexMap={dateToIndexMap}
                />
              ))}
            {verticalLines.map(line => {
              const x = dateToIndexMap
                ? dateToIndexMap.get(line.x.getTime()) ?? 0
                : line.x;
              const posX = dateToIndexMap ? xScale(x) : xScaleTime(x);
              const posY1 = yScale(yScale.domain()[0]);
              const posY2 = yScale(yScale.domain()[1]);
              return (
                <g key={line.x.toISOString()}>
                  <S.VerticalLine x1={posX} y1={posY1} x2={posX} y2={posY2} />
                </g>
              );
            })}
          </g>
        </S.Plot>
      </S.PriceChangesChartContainer>
    </S.PriceChangesChartRoot>
  );
};

PriceChangesChart.Styled = S;
